import { takeLatest, put, call, fork, select } from 'redux-saga/effects';
import callApi from 'utils/api';
import { getUrlParameters } from 'utils/helpers';
import * as actionTypes from './constants';
import {
  addUserSuccess,
  addUserFailure,
  updateUserSuccess,
  updateUserFailure,
  fetchUsersSuccess,
  fetchUsersFailure,
  fetchUsersNextSuccess,
  fetchUsersNextFailure,
  deleteUserSuccess,
  deleteUserFailure,
  updateLatestAddedUsers,
  fetchPermissionsSuccess,
  fetchPermissionsFailure,
} from './user-management-actions';

export function* doAddUser(action) {
  try {
    const results = yield call(callApi, {
      endpoint: `user/${action.payload.userId}/assign`,
      options: {
        method: 'PUT',
        errorMessage: 'Could not assign user to scope',
        retryAction: action,
      },
    });
    yield put(addUserSuccess(results));
  } catch (err) {
    yield put(addUserFailure(err));
  }
}

export function* doDeleteUser(action) {
  try {
    const results = yield call(callApi, {
      endpoint: `user/${action.payload.userId}/remove`,
      options: {
        method: 'PUT',
        errorMessage: 'Could not remove user from scope',
        retryAction: action,
      },
    });
    if (action.payload.shouldUpdateLatestAddedUsers) {
      yield put(updateLatestAddedUsers(action.payload.userId));
    }
    yield put(deleteUserSuccess(results));
  } catch (err) {
    yield put(deleteUserFailure(err));
  }
}

export function* doUpdateUser(action) {
  try {
    const results = yield call(callApi, {
      // eslint-disable-next-line
      endpoint: `user/${action.payload.userId}/updateRole?roleId=${action.payload.roleId}`,
      options: {
        method: 'PUT',
        errorMessage: `Could not update user to ${action.payload.roleId}`,
        retryAction: action,
      },
    });
    yield put(updateUserSuccess(results));
  } catch (err) {
    yield put(updateUserFailure(err));
  }
}

export function* doFetchUsers(action) {
  const { page, searchString } = action.payload;
  try {
    const results = yield call(callApi, {
      // eslint-disable-next-line
      endpoint: `user/byScope?page=${page}&pageSize=20${searchString ? `&searchString=${searchString}` : ''}`,
      options: {
        errorMessage: 'Could not get users',
        retryAction: action,
      },
    });
    yield put(fetchUsersSuccess(results));
  } catch (err) {
    yield put(fetchUsersFailure(err));
  }
}

export const getNext = state => state.userManagement.next;
export function* doFetchUsersNext(action) {
  try {
    const next = yield select(getNext);
    const result = yield call(callApi, {
      endpoint: `${next.uri.replace('api/', '/')}?${getUrlParameters({ params: next.query })}`,
      options: {
        errorMessage: `Could not next worklist data: ${next.uri}`,
        retryAction: action,
      },
    });
    yield put(fetchUsersNextSuccess(result));
  } catch (err) {
    yield put(fetchUsersNextFailure(err));
  }
}

export function* doFetchPermissions(action) {
  try {
    const results = yield call(callApi, {
      // eslint-disable-next-line
      endpoint: 'permissions',
      options: {
        errorMessage: 'Could not get permissions',
        retryAction: action,
      },
    });
    yield put(fetchPermissionsSuccess(results));
  } catch (err) {
    yield put(fetchPermissionsFailure(err));
  }
}

function* watchAddUser() {
  yield takeLatest(actionTypes.ADD_USER_REQUEST, doAddUser);
}

function* watchDeleteUser() {
  yield takeLatest(actionTypes.DELETE_USER_REQUEST, doDeleteUser);
}

function* watchUpdateUser() {
  yield takeLatest(actionTypes.UPDATE_USER_REQUEST, doUpdateUser);
}

function* watchFetchUsers() {
  yield takeLatest(actionTypes.FETCH_SCOPED_USERS_REQUEST, doFetchUsers);
}

function* watchFetchUsersNext() {
  yield takeLatest(actionTypes.FETCH_SCOPED_USERS_NEXT_REQUEST, doFetchUsersNext);
}

function* watchFetchPermissions() {
  yield takeLatest(actionTypes.GET_PERMISSIONS_REQUEST, doFetchPermissions);
}

export default [
  fork(watchAddUser),
  fork(watchDeleteUser),
  fork(watchUpdateUser),
  fork(watchFetchUsers),
  fork(watchFetchUsersNext),
  fork(watchFetchPermissions),
];
